// src/admin/lib/healthCounts.js
//
// The catalogue counts behind Health (ticket 35) and Overview (ticket 57), in
// ONE place. Overview's three boxes link to Health's tabs, so the number on the
// box and the number on the tab must come from the same filters. If each screen
// kept its own copy, the two would drift and the box would say 12 while the tab
// it opens says 13.
//
// EVERY NUMBER IS A SERVER COUNT (head: true), not the length of a loaded list.
// A count that fails comes back as null ("could not load"), which is not 0: an
// unknown must never read as "nothing wrong".
//
// is_author_described is GENERATED from description_source = 'author', so a book
// with no description_source holds NULL there, not false. "Not author-described"
// is therefore `is_author_described IS NOT TRUE`; `.eq(..., false)` would
// silently drop every one of those books. is_recommendable is different: it is
// built from IS NOT NULL tests, so it is true or false, never NULL (checked
// against every combination in Postgres on 19 Oct, ticket 57 review).

import { supabase } from '../../lib/supabase';

// A row has text to embed when either column holds something. PostgREST can't
// trim, so a whitespace-only row passes this; the batch skips such a row as
// no_description and says so.
export const HAS_TEXT = 'description.neq."",reader_description.neq.""';
// Reader copy: promoted into description (source 'reader'), or archived beside an
// author's text. set_reader_description always archives, so the second is the
// common case.
export const READER_COPY = 'description_source.eq.reader,reader_description.neq.""';

// What each Health tab shows. Each is a function that adds its filters to a
// query, so the list and the count behind its tab number are built from the SAME
// filters.
export const SETS = {
  nodata:   q => q.eq('is_author_described', true).is('embedding', null),
  stale:    q => q.eq('is_recommendable', false).not('embedding', 'is', null).or(HAS_TEXT),
  noauthor: q => q.eq('status', 'live').not('is_author_described', 'is', true),
};
export const NOAUTHOR_SUB = {
  all:    q => q,
  author: q => q.not('author_id', 'is', null),
  nobody: q => q.is('author_id', null),
};

export const countOf = (build) => build(supabase.from('books').select('id', { count: 'exact', head: true }));

// Every count either screen shows.
export const COUNT_QUERIES = {
  live:            q => q.eq('status', 'live'),
  authorDescribed: q => q.eq('status', 'live').eq('is_author_described', true),
  // Overview's "Ready for readers": live, author-described, and the stored hash
  // matches the text. A stale book is NOT ready, so it falls in the middle box
  // (notSearchableStale below) and no live book is counted twice.
  ready:           q => q.eq('status', 'live').eq('is_author_described', true).eq('is_recommendable', true),
  nodata:          SETS.nodata,
  stale:           SETS.stale,
  // Overview's middle box, "Saved but not searchable" (ticket 57). Spec section 3
  // makes the three boxes "the three states a live book can be in", each a share
  // of the LIVE catalogue, so this is Health's two tabs cut down to live books
  // (SETS.nodata and SETS.stale have no status filter, and stale does not need an
  // author description). With these, Ready + Saved but not searchable + No author
  // description = Health's "Live books", and no live book is in two boxes. Built
  // on the same SETS so the box can't drift from the tabs. Health's tabs can show
  // more: books that are not live yet, and stale books with no author description.
  notSearchableNodata: q => SETS.nodata(q).eq('status', 'live'),
  notSearchableStale:  q => SETS.stale(q).eq('status', 'live').eq('is_author_described', true),
  noauthor:        SETS.noauthor,
  noauthorAuthor:  q => NOAUTHOR_SUB.author(SETS.noauthor(q)),
  noauthorNobody:  q => NOAUTHOR_SUB.nobody(SETS.noauthor(q)),
  readerCopy:      q => SETS.noauthor(q).or(READER_COPY),
};

// Health's own eight. `ready` is Overview's alone; Health does not ask for it.
export const HEALTH_KEYS = ['live', 'authorDescribed', 'nodata', 'stale', 'noauthor', 'noauthorAuthor', 'noauthorNobody', 'readerCopy'];

// Run some or all of the counts. A failed one is stored as null, which is not 0.
export async function loadCounts(keys = Object.keys(COUNT_QUERIES), label = 'Health') {
  const results = await Promise.all(keys.map(k => countOf(COUNT_QUERIES[k])));
  const counts = {};
  let failed = 0;
  keys.forEach((k, i) => {
    const { count, error } = results[i];
    if (error || typeof count !== 'number') {
      if (error) console.error(`[${label}] count "${k}" failed:`, error.message);
      counts[k] = null;
      failed++;
    } else {
      counts[k] = count;
    }
  });
  return { counts, failed };
}
